import type { KVActiveLayer, KVTemperature } from 'features/kestrelViewer'
import { getDataLayersVisibility } from 'features/kestrelViewer/utils/visibilityUtils'
import { useEffect, useState } from 'react'
import { type GeckoKestrelModel } from 'types'

interface UseDataLayerManagementProps {
  kestrelModel: GeckoKestrelModel | undefined
}

/**
 * @internal Implementation detail of KestrelViewerProvider.
 * Do not use directly—use the context values exposed by KestrelViewerProvider.
 *
 * Manages data layer state (active layer, available prefixes, temperature).
 * Event handlers for layer switching are in KestrelInitializer.
 */
export const useDataLayerManagement = ({
  kestrelModel,
}: UseDataLayerManagementProps) => {
  const [activeLayer, setActiveLayer] = useState<KVActiveLayer>({
    prefix: 'RGB',
    type: 'rgb',
    opacity: 1.0,
  })
  const [availableLayerPrefixes, setAvailableLayerPrefixes] = useState<
    string[]
  >(['RGB'])
  const [currentTemperature, setCurrentTemperature] =
    useState<KVTemperature | null>(null)

  // Initialize layer data from kestrel model
  useEffect(() => {
    if (!getDataLayersVisibility(kestrelModel) || !kestrelModel?.data) return

    const config = kestrelModel.data.dataLayersConfig
    const prefixes = config.getAvailablePrefixes()

    setAvailableLayerPrefixes(prefixes)

    // Only initialize if activeLayer is still the default RGB
    if (activeLayer.prefix === 'RGB' && activeLayer.type === 'rgb') {
      const firstLayer = config.getFirstLayer()
      setActiveLayer({
        prefix: firstLayer.prefix,
        type: firstLayer.type,
        opacity: 1.0,
        decode_min_f: firstLayer.decodeRange?.min_f,
        decode_max_f: firstLayer.decodeRange?.max_f,
        visible_min_f: firstLayer.decodeRange?.min_f,
        visible_max_f: firstLayer.decodeRange?.max_f,
        isCustom: firstLayer.isCustom,
        customId: firstLayer.customId,
      })
    }
  }, [kestrelModel, activeLayer.prefix, activeLayer.type])

  const hasDataLayersSupport = getDataLayersVisibility(kestrelModel)

  return {
    activeLayer,
    setActiveLayer,
    availableLayerPrefixes,
    setAvailableLayerPrefixes,
    currentTemperature,
    setCurrentTemperature,
    hasDataLayersSupport,
  }
}
